import { FormikProps } from 'formik'
import { ServiceEnum } from '../interfaces/Services/ServicesEnum'
import {
  DeliveryEnum,
  PaymentModeEnum,
  PaymentModeId,
  SStatusEnum,
  ShippingMethodEnum,
} from '../interfaces'
import { PackageTypeEnum } from '../interfaces/Shipment/PackageTypeEnum'
import { WeightTypeEnum } from '../interfaces/Shipment/WeightTypeEnum'
import { ShipmentTypeEnum } from '../interfaces/Shipment/ShipmentTypeEnum'
import { ShipmentStatus } from '../modules/ShipmentV2/filterOptions'

/**
 * Format a float string value
 *
 * @param value The input value to be formatted
 * @param setValue A function that takes the formatted value and assigns it to
 *  a variable
 * @param min The minimum allowed value (default is -Infinity)
 * @param max The maximum allowed value (default is Infinity)
 * @returns A boolean indicating whether the formatting was successful or not
 */
export const formatFloat = (
  value: string,
  setValue: (f: string) => void,
  min?: number,
  max?: number
) => {
  let formatted = value

  let regex = /^(\d*\.\d{0,3}|\d*)$/

  if (!regex.test(value)) {
    return false
  }
  if (formatted.length > 1) {
    formatted = formatted.replace(/^0+/, '')
  }
  if (formatted === '') {
    formatted = '0'
  } else if (formatted[0] === '.') {
    formatted = '0' + formatted
  }

  let result = Number(formatted)
  if (!!min) {
    result = Math.max(min, result)
  }
  if (!!max) {
    result = Math.min(max, result)
  }

  if (!!min || !!max) {
    if (value[value.length - 1] === '.') {
      setValue(`${result}.`)
    } else {
      setValue(`${result}`)
    }
  } else {
    if (value[value.length - 1] === '.') {
      setValue(`${formatted.toString().padStart(2, '0')}`)
    } else {
      setValue(`${formatted.toString().padStart(2, '0')}`)
    }
  }

  return true
}

export const handleNumberChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  formik: FormikProps<any>,
  allowFloats = true
) => {
  if (event.target.value.length > 1) {
    event.target.value = event.target.value.replace(/^0+/, '')
  }
  const setValue = (value: string) => {
    event.target.value = value
  }

  if (!allowFloats) {
    event.target.value = event.target.value.replace(/[^0-9]/g, '')
  } else if (!formatFloat(event.target.value, setValue)) {
    return
  }

  formik.handleChange(event)
}

// Format name for display
export const formatName = (name: string): string => {
  name = name.toLowerCase()
  let words = name.split(' ')
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1)
  }
  let formattedText = words.join(' ')

  return formattedText
}

export const formatSentence = (text?: string | null): string => {
  const t = (text || '').toLowerCase().trim()
  return t.charAt(0).toUpperCase() + t.slice(1)
}

export const formantServices = (service: ServiceEnum) => {
  switch (service) {
    case ServiceEnum.COD:
      return 'COD'
    case ServiceEnum.CREDITO:
      return 'CREDITO'
    case ServiceEnum.CONTADO:
      return 'CONTADO'
    default:
      return 'No existe'
  }
}

export const formantPaymentMode = (payment: PaymentModeEnum) => {
  switch (payment) {
    case PaymentModeEnum.COD:
      return 'COD'
    case PaymentModeEnum.CREDIT:
      return 'CREDITO'
    case PaymentModeEnum.CONTADO:
      return 'CONTADO'
    default:
      return 'No existe'
  }
}

export const formantPaymentModeId = (payment: PaymentModeId) => {
  switch (payment?.toString()) {
    case PaymentModeId.COD:
      return 'COD'
    case PaymentModeId.CREDIT:
      return 'CREDITO'
    case PaymentModeId.CONTADO:
      return 'CONTADO'
    default:
      return 'Sin servicio, contacte a soporte'
  }
}

export const formantShippingMethod = (method: ShippingMethodEnum) => {
  switch (method) {
    case ShippingMethodEnum.SEA:
      return 'Marítimo'
    case ShippingMethodEnum.LAND:
      return 'Terrestre'
    case ShippingMethodEnum.AIR:
      return 'Aéreo'
    default:
      return 'No existe'
  }
}

export const formantPackageType = (type: PackageTypeEnum) => {
  switch (type) {
    case PackageTypeEnum.ABOUT:
      return 'Sobre'
    case PackageTypeEnum.BOX:
      return 'Caja'
    default:
      return 'No existe'
  }
}

export const formantWeightTypeEnum = (type: WeightTypeEnum) => {
  switch (type) {
    case WeightTypeEnum.KG:
      return 'Kg'
    case WeightTypeEnum.LIBRA:
      return 'Libra'
    default:
      return 'No existe'
  }
}

export const formantDeliveryEnum = (type: DeliveryEnum) => {
  switch (type) {
    case DeliveryEnum.OFFICE:
      return 'Tienda'
    case DeliveryEnum.DELIVERY:
      return 'Domicilio'
    default:
      return 'No existe'
  }
}

export const formantShipmentTypeEnum = (type: ShipmentTypeEnum) => {
  switch (type) {
    case ShipmentTypeEnum.STORE:
      return 'Tienda'
    case ShipmentTypeEnum.DELIVERY:
      return 'Domicilio'
    default:
      return 'No existe'
  }
}

export const formantStatusTypeEnum = (type: SStatusEnum) => {
  switch (type) {
    case SStatusEnum.ACTIVE:
      return 'Activo'
    case SStatusEnum.INACTIVE:
      return 'Inactivo'
    default:
      return 'No existe'
  }
}

export const formatAmount = (amount: number) => {
  return (+amount.toFixed(2)).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
  })
}

export const obtenerCodigoArea = (numero: string): string => {
  const numLimpio = numero ?? ''.replace(/\D/g, '')

  const codigosMoviles = ['412', '422', '424', '414', '426', '416']
  const codigosMovilesCon0 = codigosMoviles.map((cod) => `0${cod}`)

  const codigo4digitos = numLimpio.substring(0, 4)
  if (codigosMovilesCon0.includes(codigo4digitos)) {
    return codigo4digitos.substring(1)
  }

  const codigo3digitos = numLimpio.substring(0, 3)
  if (codigosMoviles.includes(codigo3digitos)) {
    return codigo3digitos
  }
  return 'FIJO'
}

export const obtenerNumero = (numero: string): string => {
  const numLimpio = numero.replace(/\D/g, '')

  const codigosMoviles = ['412', '422', '424', '414', '426', '416']
  const codigosMovilesCon0 = codigosMoviles.map((cod) => `0${cod}`)

  const codigo4digitos = numLimpio.substring(0, 4)
  if (codigosMovilesCon0.includes(codigo4digitos)) {
    return numLimpio.substring(4)
  }

  const codigo3digitos = numLimpio.substring(0, 3)
  if (codigosMoviles.includes(codigo3digitos)) {
    return numLimpio.substring(3)
  }

  return numLimpio
}

export const formantServicesEnum = (services: ServiceEnum) => {
  switch (services) {
    case ServiceEnum.STANDARD:
      return 'Estándar'
    case ServiceEnum.DOCUMENT:
      return 'Sobre'
    case ServiceEnum.INTERNATIONAL:
      return 'Internacional'
    case ServiceEnum.NATIONAL_LOCKER:
      return 'Casillero Nacional'
    case ServiceEnum.ECONOMIC:
      return 'Económico'
    default:
      return 'Desconocido'
  }
}

export const shipmentStatusFormat = (status: ShipmentStatus) => {
  switch (status) {
    case ShipmentStatus.DRAFT:
      return 'En Creación'
    case ShipmentStatus.ACTIVE:
      return 'Activa'
    case ShipmentStatus.ANNULLED:
      return 'Anulada'
    case ShipmentStatus.REPLACED:
      return 'Sustituida'
    case ShipmentStatus.DELIVERED:
      return 'Entregada'
    default:
      return 'Desconocida'
  }
}

/**
 * Fechas del backend Django para mostrar en pantalla.
 *
 * Reglas comunes a los dos formatos:
 *  - null/undefined/'' → guion, no "Invalid Date".
 *  - String que no parsea → se devuelve tal cual. Mejor mostrar el crudo que
 *    inventar una fecha; si el backend cambia el formato, se ve.
 *
 * Ojo con la zona horaria: el backend manda `"2026-08-04T12:30:00"` SIN sufijo,
 * así que `new Date()` lo interpreta como hora LOCAL, no UTC. Es lo que se
 * quiere: es hora de Venezuela y el operador la lee en su propio huso.
 */
const parseBackendDate = (dt?: string | null): Date | null => {
  if (!dt) return null
  const d = new Date(dt)
  return isNaN(d.getTime()) ? null : d
}

/** Fecha y hora, ej. "04/08/2026, 12:30". */
export function formatDateTime(dt?: string | null): string {
  if (!dt) return '–'
  const d = parseBackendDate(dt)
  if (!d) return dt
  return d.toLocaleString('es-VE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Solo fecha, ej. "04/08/2026". */
export function formatDate(dt?: string | null): string {
  if (!dt) return '–'
  const d = parseBackendDate(dt)
  if (!d) return dt
  return d.toLocaleDateString('es-VE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}
